// src/hooks/useAuthState.js
import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../utils/supabase/supabaseClient";

const DEV = process.env.NODE_ENV !== "production";

export function useAuthState() {
  const isBrowser = typeof window !== "undefined";

  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [planName, setPlanName] = useState("guest");

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* =========================================================
     Shared helper: fetch effective plan
     subscriptions = billing truth
     profiles = fallback only
  ========================================================= */
const fetchEffectivePlan = useCallback(async (userId) => {
if (!userId) return "guest";

try {
// 1️⃣ Canonical UI truth = profiles
const { data: profileData } = await supabase
.from("profiles")
.select("plan")
.eq("id", userId)
.maybeSingle();

```
if (profileData?.plan) {
  return String(profileData.plan).toLowerCase();
}

// 2️⃣ Billing fallback
const { data: subData } = await supabase
  .from("subscriptions")
  .select("plan")
  .eq("user_id", userId)
  .maybeSingle();

if (subData?.plan) {
  return String(subData.plan).toLowerCase();
}

return "guest";
```

} catch (err) {
if (DEV) console.warn("[useAuthState] fetchEffectivePlan failed:", err);
return "guest";
}
}, []);


  /* =========================================================
     1) Bootstrap session once
  ========================================================= */
  useEffect(() => {
    if (!isBrowser) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();

        if (cancelled || !mountedRef.current) return;

        setUser(data?.session?.user ?? null);
        setAuthReady(true);
      } catch (err) {
        if (DEV) console.warn("[useAuthState] getSession failed:", err);

        if (!cancelled && mountedRef.current) {
          setAuthReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isBrowser]);

  /* =========================================================
     2) Auth listener
  ========================================================= */
  useEffect(() => {
    if (!isBrowser) return;

    const { data } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!mountedRef.current) return;

      setUser(session?.user ?? null);
      setAuthReady(true);
    });

    return () => {
      data?.subscription?.unsubscribe?.();
    };
  }, [isBrowser]);

  /* =========================================================
     3) Load plan when auth ready
  ========================================================= */
  useEffect(() => {
    if (!isBrowser) return;

if (!authReady) return;

if (!user?.id) {
setPlanName("guest");
if (DEV) window.currentPlanName = "guest";
return;
}

    let cancelled = false;

    (async () => {
      const plan = await fetchEffectivePlan(user.id);

      if (cancelled || !mountedRef.current) return;

      setPlanName(plan);

      if (DEV) {
        window.currentPlanName = plan;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isBrowser, authReady, user?.id, fetchEffectivePlan]);

  /* =========================================================
     4) Manual refresh event
  ========================================================= */
  useEffect(() => {
    if (!isBrowser) return;

    const handler = async () => {
      if (!user?.id) return;

      const plan = await fetchEffectivePlan(user.id);

      if (!mountedRef.current) return;

      setPlanName(plan);

      if (DEV) {
        window.currentPlanName = plan;
      }
    };

    window.addEventListener("refresh-plan", handler);

    return () => {
      window.removeEventListener("refresh-plan", handler);
    };
  }, [isBrowser, user?.id, fetchEffectivePlan]);

  return {
    authReady: !!authReady,
    user: user ?? null,
    userId: user?.id ?? null,
    planName: planName || "guest",
  };
}